import {
  retargetRagScript,
  type PendingKnowledgeSetup,
} from './knowledge-keys';

export const RAG_SEARCH_COMMAND = 'GetRAGSearchResponse';
export const RAG_SCRIPT_PATH = `services/${RAG_SEARCH_COMMAND}.py`;

/** What the run needs from the app — injected, so the order is testable. */
export interface KnowledgeSetupDeps {
  /** Uploads one file into the draft version's `services/`. */
  uploadScript: (file: File) => Promise<void>;
  /** A draft file's text (throws when it isn't there). */
  readScript: (path: string) => Promise<string>;
  /** `POST /ai/knowledge/<route>` → its JSON body, or throws its `detail`. */
  post: <T>(route: 'corpus' | 'file', body: unknown) => Promise<T>;
  /** Sets the search node's corpus ids and saves the row. */
  setCorpusId: (corpusId: string) => void;
  /** The search node is there to receive a corpus. */
  hasSearchNode: () => boolean;
  progress: (text: string) => void;
}

export interface KnowledgeSetupResult {
  keysAdded: number;
  corpusId?: string;
  projectId?: string;
  documents: number;
  scriptRetargeted: boolean;
}

const toBase64 = async (file: File) => {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
};

/**
 * After Talk it through creates the solution: keys into `services/`, then —
 * when references were attached — a corpus built from them in the chosen
 * key's project, the search script pointed at that project and key, and the
 * corpus id written onto the search node (which is what releases the first
 * auto-publish; see the `live_corpus_id` subscription in `table-store.ts`).
 */
export const runKnowledgeSetup = async (
  setup: PendingKnowledgeSetup,
  deps: KnowledgeSetupDeps
): Promise<KnowledgeSetupResult> => {
  const result: KnowledgeSetupResult = {
    keysAdded: 0,
    documents: 0,
    scriptRetargeted: false,
  };

  for (const key of setup.keys) {
    deps.progress(`Adding ${key.name}…`);
    // eslint-disable-next-line no-await-in-loop -- one upload at a time, as Actions does
    await deps.uploadScript(
      new File([key.text], key.name, { type: 'text/x-python' })
    );
    result.keysAdded += 1;
  }

  const key = setup.keys[setup.corpusKey] ?? setup.keys[0];
  if (!key || !setup.references.length || !deps.hasSearchNode()) {
    return result;
  }

  deps.progress(`Creating the knowledge base in ${key.projectId}…`);
  const corpus = await deps.post<{
    corpus: string;
    corpusId: string;
    projectId: string;
  }>('corpus', {
    credentials: key.text,
    displayName: `${setup.title} knowledge`.slice(0, 120),
    description: `Created by Pro Studio from ${setup.references.length} reference(s).`,
  });
  result.corpusId = corpus.corpusId;
  result.projectId = corpus.projectId;

  for (const file of setup.references) {
    deps.progress(`Adding ${file.name} to the knowledge base…`);
    // eslint-disable-next-line no-await-in-loop -- one per request: the serverless time limit
    await deps.post('file', {
      credentials: key.text,
      corpus: corpus.corpus,
      file: {
        name: file.name,
        mimeType: file.type || 'text/plain',
        base64: await toBase64(file),
      },
    });
    result.documents += 1;
  }

  const script = await deps.readScript(RAG_SCRIPT_PATH);
  const retargeted = retargetRagScript(script, {
    projectId: corpus.projectId,
    keyFileName: key.name,
  });
  if (retargeted) {
    deps.progress('Pointing the knowledge search at it…');
    await deps.uploadScript(
      new File([retargeted], `${RAG_SEARCH_COMMAND}.py`, {
        type: 'text/x-python',
      })
    );
    result.scriptRetargeted = true;
  }

  deps.setCorpusId(corpus.corpusId);
  return result;
};
